import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useDispatch, useSelector } from 'react-redux';
import { CommunicationMatrixForm } from '../components/CommunicationMatrixForm';
import { DataTable } from '../components/DataTable';
import { DateCell } from '../components/DateCell';
import {
  CommunicationMatrixItem,
  CommunicationMatrixPayload,
} from '../features/communication-matrix/types';
import {
  loadMatrix,
  createMatrix,
  updateMatrix,
  deleteMatrix,
  setSearch,
  setFilterWho,
  setFilterToWhom,
} from '../features/communication-matrix/communicationMatrixSlice';
import { loadRoles } from '../features/roles/rolesSlice';
import { selectCurrentUser } from '../features/auth/authSlice';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { RootState, AppDispatch } from '../store/store';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { showToast } from '../components/ui/toast.utils';

const columnHelper = createColumnHelper<CommunicationMatrixItem>();

function isPrivilegedRole(role: string): boolean {
  return role === 'superadmin' || role === 'admin';
}

function canMutateMatrixItem(
  item: CommunicationMatrixItem,
  userRole: string,
  userId: number | undefined,
): boolean {
  if (isPrivilegedRole(userRole)) return true;
  return item.createdById != null && item.createdById === userId;
}

function renderCreatedAtCell(info: { getValue: () => string }) {
  return <DateCell value={info.getValue()} />;
}

interface ActionsCellProps {
  item: CommunicationMatrixItem;
  userRole: string;
  userId: number | undefined;
  platformCanMutate: boolean;
  onEdit: (item: CommunicationMatrixItem) => void;
  onDelete: (id: string) => void;
}

function ActionsCell({
  item,
  userRole,
  userId,
  platformCanMutate,
  onEdit,
  onDelete,
}: ActionsCellProps) {
  if (!platformCanMutate) {
    return <span className="text-sm text-muted-foreground">Solo lectura</span>;
  }
  const canEdit = canMutateMatrixItem(item, userRole, userId);

  if (!canEdit) {
    return <span className="text-sm text-muted-foreground">Sin permisos</span>;
  }

  return (
    <div className="flex gap-2">
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
        onClick={() => onEdit(item)}
      >
        Editar
      </button>
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
        onClick={() => onDelete(item.id)}
      >
        Eliminar
      </button>
    </div>
  );
}

export function CommunicationMatrixPage() {
  const { confirm } = useConfirmDialog();
  const { canEdit: platformCanMutate } = usePlatformAccessLevel('communication_matrix.access');
  const dispatch = useDispatch<AppDispatch>();
  const {
    items,
    loading,
    error,
    pagination,
    search,
    filterWho,
    filterToWhom,
    sortBy,
    sortDir,
  } = useSelector((state: RootState) => state.communicationMatrix);

  const { items: roles, loading: rolesLoading } = useSelector((state: RootState) => state.roles);
  const currentUser = useSelector(selectCurrentUser);
  const isPrivileged = isPrivilegedRole(currentUser?.role ?? '');
  const userRole = currentUser?.role || '';
  const userId = currentUser?.id;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CommunicationMatrixItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const roleNames = useMemo(() => roles.map((role) => role.name), [roles]);

  useEffect(() => {
    dispatch(loadRoles({ pageSize: 200 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      loadMatrix({
        search,
        page: pagination.page,
        pageSize: pagination.pageSize,
        who: filterWho,
        toWhom: filterToWhom,
        sortBy,
        sortDir,
      }),
    );
  }, [dispatch, search, pagination.page, pagination.pageSize, filterWho, filterToWhom, sortBy, sortDir]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditing(null);
    }
  }, []);

  const handleSubmit = useCallback(
    async (values: CommunicationMatrixPayload) => {
      if (!platformCanMutate) return;
      setSubmitting(true);
      try {
        if (editing) {
          const result = await dispatch(updateMatrix({ id: editing.id, payload: values }));
          if (updateMatrix.fulfilled.match(result)) {
            setDialogOpen(false);
          }
        } else {
          const result = await dispatch(createMatrix(values));
          if (createMatrix.fulfilled.match(result)) {
            setDialogOpen(false);
          }
        }
      } catch (err) {
        console.error('Error saving item:', err);
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, editing, platformCanMutate],
  );

  const handleEdit = useCallback(
    (item: CommunicationMatrixItem) => {
      if (!platformCanMutate) return;
      if (!canMutateMatrixItem(item, userRole, userId)) {
        showToast('No tenés permiso para editar este registro', 'warning');
        return;
      }
      setEditing(item);
      setDialogOpen(true);
    },
    [platformCanMutate, userRole, userId],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!platformCanMutate) return;
      const item = items.find((i) => i.id === id);
      if (item && !canMutateMatrixItem(item, userRole, userId)) {
        showToast('No tenés permiso para eliminar este registro', 'warning');
        return;
      }

      const ok = await confirm({
        title: 'Eliminar registro',
        message: '¿Estás seguro de que quieres eliminar este registro?',
        confirmText: 'Eliminar',
        variant: 'danger',
      });
      if (!ok) return;

      try {
        await dispatch(deleteMatrix(id));
      } catch (err) {
        console.error('Error deleting item:', err);
      }
    },
    [confirm, dispatch, items, platformCanMutate, userRole, userId],
  );

  const actionsCellRenderer = useCallback(
    (info: { row: { original: CommunicationMatrixItem } }) => (
      <ActionsCell
        item={info.row.original}
        userRole={userRole}
        userId={userId}
        platformCanMutate={platformCanMutate}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ),
    [userRole, userId, platformCanMutate, handleEdit, handleDelete],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('whoCommunicates', {
        header: 'Quién Comunica',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('whatCommunicates', {
        header: 'Qué Comunica',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('toWhomCommunicates', {
        header: 'A Quién Comunica',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('whenCommunicates', {
        header: 'Cuándo Comunica',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('howCommunicates', {
        header: 'Cómo Comunica',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('createdAt', { header: 'Creado', cell: renderCreatedAtCell }),
      columnHelper.display({ id: 'actions', header: 'Acciones', cell: actionsCellRenderer }),
    ],
    [actionsCellRenderer],
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Matriz de Comunicación</h1>
        {platformCanMutate && (
          <button
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={openCreate}
          >
            + Nuevo registro
          </button>
        )}
      </div>

      <div>
        {rolesLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
            <p className="text-sm text-muted-foreground mt-2">Cargando roles...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Buscar</label>
              <input
                type="text"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
                placeholder="Buscar por contenido o rol…"
                value={search}
                onChange={(e) => dispatch(setSearch(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Quién comunica</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                value={filterWho}
                onChange={(e) => dispatch(setFilterWho(e.target.value))}
              >
                <option value="" className="bg-background text-foreground">
                  Todos
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name} className="bg-background text-foreground">
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">A quién comunica</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                value={filterToWhom}
                onChange={(e) => dispatch(setFilterToWhom(e.target.value))}
              >
                <option value="" className="bg-background text-foreground">
                  Todos
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name} className="bg-background text-foreground">
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div>
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
            {error}
          </div>
        )}
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No hay registros de comunicación"
          pageSizeOptions={[5, 10, 20, 50]}
        />
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar registro' : 'Nuevo registro'}</DialogTitle>
          </DialogHeader>
          {rolesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Cargando roles...</p>
            </div>
          ) : (
            <CommunicationMatrixForm
              roleOptions={roleNames}
              initialValues={editing}
              onSubmit={handleSubmit}
              onCancel={() => handleDialogOpenChange(false)}
              loading={submitting}
              userRole={userRole}
              isSuperadmin={isPrivileged}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
